/**
 * use-available-work — TanStack Query hook for the right-rail panel on
 * the Timesheet page. Drives the infinite-scroll list of cards the caller
 * can log time against right now (tasks/tickets in Today/Review where
 * they are assignee, reviewer or collaborator, plus today's events (in
 * the viewer's time zone) where they are creator or attendee).
 *
 *   useAvailableWork({ q, projectId, kind, enabled })
 *     → useInfiniteQuery on
 *       ['available-work', q ?? '', projectId ?? '', kind ?? 'all']
 *
 * The flat shape returned (`items`, `hasNextPage`, etc.) is what the
 * panel renders directly; callers don't need to know the pages live in
 * `data.pages[]`.
 */

import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { apiClient } from '@/api/client'
import type { AvailableWorkItem, AvailableWorkPayload } from '@/api/types-bridge'

interface UseAvailableWorkArgs {
  q?: string
  projectId?: string
  kind?: 'task' | 'ticket' | 'event'
  /** Pause when the panel is collapsed. Defaults to true. */
  enabled?: boolean
  /** Page size. Legacy default 20; cap at 50 server-side. */
  perPage?: number
}

/** Root key shared by every filter variant, for family-wide invalidation. */
export const AVAILABLE_WORK_QUERY_ROOT = ['available-work'] as const

export function availableWorkQueryKey(args: UseAvailableWorkArgs) {
  return [
    ...AVAILABLE_WORK_QUERY_ROOT,
    args.q ?? '',
    args.projectId ?? '',
    args.kind ?? 'all',
  ] as const
}

export function useAvailableWork({
  q,
  projectId,
  kind,
  enabled = true,
  perPage = 20,
}: UseAvailableWorkArgs = {}) {
  const query = useInfiniteQuery<
    AvailableWorkPayload,
    Error,
    { pages: AvailableWorkPayload[]; pageParams: number[] },
    ReturnType<typeof availableWorkQueryKey>,
    number
  >({
    queryKey: availableWorkQueryKey({ q, projectId, kind }),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams()
      if (q && q.trim().length > 0) params.set('q', q.trim())
      if (projectId) params.set('projectId', projectId)
      if (kind) params.set('kind', kind)
      params.set('page', String(pageParam))
      params.set('perPage', String(perPage))
      const res = await apiClient<{ data: AvailableWorkPayload }>(
        `/api/work/available?${params.toString()}`,
      )
      return res.data
    },
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

  // Flatten pages → items for ergonomic consumption.
  const items = useMemo<AvailableWorkItem[]>(() => {
    if (!query.data) return []
    return query.data.pages.flatMap((p) => p.items)
  }, [query.data])

  return {
    items,
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}
